import { useMemo, useState } from "react";
import { useAccount } from "wagmi";
import { useOwnerTerritories } from "./useOwnerTerritories";
import { useTerritoryPreviews } from "./useTerritoryPreviews";
import { MyTerritories } from "./MyTerritories";
import { TerritoryDetail } from "./TerritoryDetail";
import { useModerationNotifications } from "../useModerationNotifications";
import { OnboardingEntryBanner } from "../onboarding/OnboardingEntryBanner";
import { useI18n } from "../../i18n";
import { cn } from "../cn";

const EMPTY: never[] = [];

/**
 * DashboardRoute — authenticated OWNER MANAGEMENT surface (#dashboard).
 *
 * Auth boundary is enforced by useOwnerTerritories (connected + valid SIWE
 * session + identity match). When not authenticated it renders an
 * auth-required state and never exposes owner data.
 */
export function DashboardRoute() {
  const { t } = useI18n();
  const { address, isConnected } = useAccount();
  const state = useOwnerTerritories();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const short = address ? `${address.slice(0, 6)}…${address.slice(-4)}` : "";

  const selected = useMemo(() => {
    if (state.status !== "resolved") return null;
    const idx = state.territories.findIndex((t) => t.key === selectedKey);
    return idx >= 0 ? { territory: state.territories[idx], ordinal: idx + 1 } : null;
  }, [state, selectedKey]);

  const territories = state.status === "resolved" ? state.territories : EMPTY;
  const previews = useTerritoryPreviews(territories);
  const { ownerUnreadTargets } = useModerationNotifications();
  // Territory identity_ids the owner has unread moderation activity on.
  const unreadIdentityIds = useMemo(() => {
    const s = new Set<string>();
    for (const key of ownerUnreadTargets) {
      const [type, id] = key.split(":");
      if (type === "territory" && id) s.add(id);
    }
    return s;
  }, [ownerUnreadTargets]);

  return (
    <div
      data-testid="dashboard-route"
      className="w-full h-full overflow-y-auto bg-obsidian"
    >
      <div className="mx-auto w-[min(1180px,100%)] px-5 md:px-8 py-8">
        {/* Header */}
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="m-0 font-display text-[22px] font-semibold tracking-[-0.01em] text-text-primary">
              {t("dashboard.route.title")}
            </h1>
            <p className="mt-1 text-[12px] text-text-secondary font-mono tracking-[0.04em]">
              {t("dashboard.route.subtitle")}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isConnected && address && (
              <span
                data-testid="dashboard-wallet-address"
                className="inline-flex items-center gap-2 h-7 px-3 neon-glass pill-ghost font-mono text-[10px] text-text-primary"
              >
                <span
                  aria-hidden
                  className="h-1.5 w-1.5 rounded-pill bg-state-success"
                />
                {short}
              </span>
            )}
            <a
              data-testid="dashboard-back-to-grid"
              href="#grid"
              className={cn(
                "inline-flex items-center gap-2 h-7 px-3 no-underline",
                "font-mono text-[10px] font-medium tracking-[0.16em] uppercase",
                "text-text-primary neon-glass pill-ghost",
              )}
            >
              {t("dashboard.route.backToGrid")}
            </a>
          </div>
        </header>

        <hr className="my-6 h-px border-0 bg-border-soft" />

        <OnboardingEntryBanner className="mb-6" />

        {state.status === "auth_required" ? (
          <div
            data-testid="dashboard-auth-required"
            className="rounded-lg border border-border-soft bg-panel p-8"
          >
            <p className="m-0 text-[15px] text-text-primary font-display">
              {t("dashboard.route.authTitle")}
            </p>
            <p className="mt-2 text-[12px] text-text-muted">
              {t("dashboard.route.authBody")}
            </p>
            <a
              data-testid="dashboard-signin-link"
              href="#auth"
              className={cn(
                "mt-5 inline-flex items-center gap-2 h-9 px-4 no-underline",
                "font-mono text-[11px] font-medium tracking-[0.16em] uppercase",
                "text-text-inverse bg-text-primary rounded-md hover:opacity-90",
              )}
            >
              {t("dashboard.route.signIn")}
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,440px)_minmax(0,1fr)] gap-6 items-start">
            <MyTerritories
              state={state}
              previews={previews}
              selectedKey={selectedKey}
              unreadIdentityIds={unreadIdentityIds}
              onOpen={setSelectedKey}
            />
            <div data-testid="dashboard-detail-slot">
              {selected ? (
                <TerritoryDetail
                  ordinal={selected.ordinal}
                  territory={selected.territory}
                  preview={previews.get(selected.territory.key)}
                />
              ) : (
                state.status === "resolved" && (
                  <div
                    data-testid="dashboard-detail-empty"
                    className="rounded-lg border border-dashed border-border-soft p-6 text-[12px] text-text-muted font-mono"
                  >
                    {t("dashboard.route.detailEmpty")}
                  </div>
                )
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
